/**
 * Scene flow engine for flat v2 scenarios (1 block = 1 scene).
 */

import type {
  Chapter,
  Scene,
  Scenario,
  GameState,
  ChoiceOption,
  ChoiceScene,
  CommandScene,
  DialogueScene,
  SceneElement,
  PhoneCallReplyOption,
  PhoneChatReplyOption,
} from '../types';
import { evaluateCondition } from './condition-evaluator';

export interface LocatedScene {
  scene: Scene;
  parentArray: Scene[];
  indexInParent: number;
}

export interface VisualState {
  background?: string;
  /** Present only when the scene that set `background` also set `backgroundLoop`. */
  backgroundLoop?: boolean;
  elements?: SceneElement[];
}

const PHONE_TYPES = new Set(['phoneCall', 'phoneChat', 'phoneMail', 'phoneMailInteract']);

function isPhoneType(type: string): boolean {
  return PHONE_TYPES.has(type);
}

export function findLocatedScene(scenes: Scene[], sceneId: string): LocatedScene | null {
  for (let i = 0; i < scenes.length; i++) {
    const scene = scenes[i]!;
    if (scene.id === sceneId) {
      return { scene, parentArray: scenes, indexInParent: i };
    }
    if (scene.type === 'choice') {
      for (const option of scene.options) {
        if (option.scenes?.length) {
          const found = findLocatedScene(option.scenes, sceneId);
          if (found) return found;
        }
      }
    }
  }
  return null;
}

export function getSceneById(scenario: Scenario, sceneId: string): Scene | null {
  return findLocatedScene(scenario.scenes, sceneId)?.scene ?? null;
}

/** Last spoken line before a choice, so the réplique stays visible above the tiles. */
export function findPrecedingDialogue(scenario: Scenario, sceneId: string): DialogueScene | null {
  const located = findLocatedScene(scenario.scenes, sceneId);
  if (!located) return null;
  for (let i = located.indexInParent - 1; i >= 0; i--) {
    const scene = located.parentArray[i]!;
    if (scene.type === 'dialogue') return scene;
    if (scene.type === 'command') continue;
    break;
  }
  return null;
}

export function collectAllSceneIds(scenes: Scene[], collected: string[] = []): string[] {
  for (const scene of scenes) {
    collected.push(scene.id);
    if (scene.type === 'choice') {
      for (const option of scene.options) {
        if (option.scenes?.length) {
          collectAllSceneIds(option.scenes, collected);
        }
      }
    }
  }
  return collected;
}

/** Walk spine from root to target along implicit linear flow, collecting scene ids. */
export function buildSpine(scenario: Scenario, sceneId: string): string[] {
  const spine: string[] = [];
  function walk(scenes: Scene[], target: string, path: string[]): boolean {
    let linearPath = path;
    for (const scene of scenes) {
      linearPath = [...linearPath, scene.id];
      if (scene.id === target) {
        spine.push(...linearPath);
        return true;
      }
      if (scene.type === 'choice') {
        for (const option of scene.options) {
          if (option.scenes?.length && walk(option.scenes, target, linearPath)) {
            return true;
          }
        }
      }
    }
    return false;
  }
  walk(scenario.scenes, sceneId, []);
  return spine;
}

export function resolveVisualState(scenario: Scenario, sceneId: string): VisualState {
  const spine = buildSpine(scenario, sceneId);
  let background: string | undefined;
  let backgroundLoop: boolean | undefined;
  for (const id of spine) {
    const scene = getSceneById(scenario, id);
    if (!scene) continue;
    if (scene.background !== undefined) {
      background = scene.background;
      backgroundLoop = scene.backgroundLoop;
    }
  }
  const currentScene = getSceneById(scenario, sceneId);
  const elements = currentScene?.elements;
  return {
    background,
    elements,
    ...(backgroundLoop !== undefined ? { backgroundLoop } : {}),
  };
}

function getImplicitNext(located: LocatedScene): string | null {
  const { parentArray, indexInParent, scene } = located;
  if (indexInParent < parentArray.length - 1) {
    return parentArray[indexInParent + 1]!.id;
  }
  const parentChoice = findParentChoiceScene(scenarioScenesRef, scene.id);
  if (!parentChoice) return null;
  return getImplicitNextAfterBlock(parentChoice.choiceLocated);
}

let scenarioScenesRef: Scene[] = [];

function findParentChoiceScene(
  rootScenes: Scene[],
  targetId: string
): { choiceLocated: LocatedScene; optionIndex: number } | null {
  function search(scenes: Scene[], _parentLocated: LocatedScene | null): ReturnType<typeof findParentChoiceScene> {
    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i]!;
      const currentLocated: LocatedScene = { scene, parentArray: scenes, indexInParent: i };
      if (scene.type === 'choice') {
        for (let oi = 0; oi < scene.options.length; oi++) {
          const option = scene.options[oi]!;
          if (option.scenes?.some((s) => s.id === targetId)) {
            return { choiceLocated: currentLocated, optionIndex: oi };
          }
          if (option.scenes?.length) {
            const nested = search(option.scenes, currentLocated);
            if (nested) return nested;
          }
        }
      }
    }
    return null;
  }
  return search(rootScenes, null);
}

function getImplicitNextAfterBlock(choiceLocated: LocatedScene): string | null {
  const { parentArray, indexInParent } = choiceLocated;
  if (indexInParent < parentArray.length - 1) {
    return parentArray[indexInParent + 1]!.id;
  }
  const parentChoice = findParentChoiceScene(scenarioScenesRef, choiceLocated.scene.id);
  if (!parentChoice) return null;
  return getImplicitNextAfterBlock(parentChoice.choiceLocated);
}

export function resolveNextSceneId(scenario: Scenario, sceneId: string): string | null {
  scenarioScenesRef = scenario.scenes;
  const located = findLocatedScene(scenario.scenes, sceneId);
  if (!located) return null;
  const scene = located.scene;
  if (scene.goto) return scene.goto;
  return getImplicitNext(located);
}

/** Next scene in the linear flow, ignoring `goto` (used when skipping an unmet `condition`). */
export function resolveImplicitNextSceneId(scenario: Scenario, sceneId: string): string | null {
  scenarioScenesRef = scenario.scenes;
  const located = findLocatedScene(scenario.scenes, sceneId);
  if (!located) return null;
  return getImplicitNext(located);
}

/** True when the scene has no condition, or the condition evaluates true. */
export function sceneConditionIsMet(scene: Scene, gameState: GameState): boolean {
  if (!scene.condition) return true;
  try {
    return evaluateCondition(scene.condition, gameState);
  } catch {
    return false;
  }
}

export function isCommandScene(scene: Scene | null): scene is CommandScene {
  return scene?.type === 'command';
}

export function skipCommandScenes(scenario: Scenario, sceneId: string): string {
  let currentId = sceneId;
  let safety = 0;
  while (safety++ < 200) {
    const scene = getSceneById(scenario, currentId);
    if (!scene || !isCommandScene(scene)) return currentId;
    const next = resolveNextSceneId(scenario, currentId);
    if (!next || next === currentId) return currentId;
    currentId = next;
  }
  return currentId;
}

export function resolvePhoneStateTarget(
  scenario: Scenario,
  sceneId: string
): 0 | 1 | 2 {
  const scene = getSceneById(scenario, sceneId);
  if (!scene) return 0;
  if (isPhoneType(scene.type)) return 2;
  if (scene.type === 'choice' && scene.phoneMailPreview) return 2;

  const nextId = resolveNextSceneId(scenario, sceneId);
  if (nextId) {
    const nextScene = getSceneById(scenario, nextId);
    if (nextScene && isPhoneType(nextScene.type) && scene.type === 'dialogue') {
      return 1;
    }
  }
  return 0;
}

export function syncPhoneState(scenario: Scenario, gameState: GameState): GameState {
  const target = resolvePhoneStateTarget(scenario, gameState.currentSceneId);
  const current = (gameState.variables?.phoneState as number) ?? 0;
  if (current === target) return gameState;
  return {
    ...gameState,
    variables: { ...gameState.variables, phoneState: target },
  };
}

export function getChapterSceneIds(scenario: Scenario, chapterStartSceneId: string): string[] {
  const chapters = scenario.metadata.chapters ?? [];
  const sorted = [...chapters].sort((a, b) => {
    const ai = scenario.scenes.findIndex((s) => s.id === a.startScene);
    const bi = scenario.scenes.findIndex((s) => s.id === b.startScene);
    return ai - bi;
  });
  const chapterIndex = sorted.findIndex((c) => c.startScene === chapterStartSceneId);
  if (chapterIndex < 0) return [];
  const startIdx = scenario.scenes.findIndex((s) => s.id === chapterStartSceneId);
  if (startIdx < 0) return [];
  let endIdx = scenario.scenes.length;
  if (chapterIndex + 1 < sorted.length) {
    const nextStart = sorted[chapterIndex + 1]!.startScene;
    const nextIdx = scenario.scenes.findIndex((s) => s.id === nextStart);
    if (nextIdx > startIdx) endIdx = nextIdx;
  }
  return scenario.scenes.slice(startIdx, endIdx).map((s) => s.id);
}

/** Chapter whose startScene is the latest one at or before this scene in the linear list. */
export function findChapterContainingScene(
  scenario: Pick<Scenario, 'scenes' | 'metadata'>,
  sceneId: string,
): Chapter | undefined {
  const chapters = scenario.metadata.chapters ?? [];
  if (!chapters.length || !sceneId) return undefined;

  const sceneIndexById = new Map(scenario.scenes.map((scene, index) => [scene.id, index]));
  const currentIndex = sceneIndexById.get(sceneId);
  if (currentIndex === undefined) return undefined;

  const sorted = chapters
    .filter((chapter) => chapter.startScene)
    .map((chapter) => ({
      chapter,
      startIndex: sceneIndexById.get(chapter.startScene) ?? Number.MAX_SAFE_INTEGER,
    }))
    .filter((entry) => entry.startIndex !== Number.MAX_SAFE_INTEGER)
    .sort((a, b) => a.startIndex - b.startIndex);

  let resolved: Chapter | undefined;
  for (const entry of sorted) {
    if (entry.startIndex <= currentIndex) {
      resolved = entry.chapter;
    } else {
      break;
    }
  }
  return resolved;
}

export function calculateChapterProgress(
  scenario: Scenario,
  sceneId: string
): { currentIndex: number; totalItems: number; percentage: number } {
  const chapter = findChapterContainingScene(scenario, sceneId);
  if (!chapter) {
    return { currentIndex: 0, totalItems: scenario.scenes.length, percentage: 0 };
  }
  const ids = getChapterSceneIds(scenario, chapter.startScene);
  const currentIndex = ids.indexOf(sceneId);
  const totalItems = ids.length;
  const percentage = totalItems > 0 ? Math.round((Math.max(currentIndex, 0) / totalItems) * 100) : 0;
  return { currentIndex: Math.max(currentIndex, 0), totalItems, percentage };
}

export function resolveChoiceEntry(
  scenario: Scenario,
  option: ChoiceOption
): string | null {
  if (option.goto) return option.goto;
  if (option.scenes?.length) {
    const firstId = option.scenes[0]!.id;
    return skipCommandScenes(scenario, firstId);
  }
  return null;
}

export function resolvePhoneReplyExit(
  scenario: Scenario,
  phoneSceneId: string,
  option: PhoneCallReplyOption | PhoneChatReplyOption
): string | null {
  if (option.goto) return option.goto;
  if (option.replyOptions?.length) return null;
  return resolveNextSceneId(scenario, phoneSceneId);
}

export function isChoiceScene(scene: Scene | null): scene is ChoiceScene {
  return scene?.type === 'choice';
}
